/**
 * Kreisdiagramm zum Aufteilen (z. B. Speicher eines Projekts unter den
 * Mitgliedern). Die Grenzen zwischen zwei Anteilen lassen sich mit dem
 * Finger oder der Maus ziehen, per Tastatur in 1-%-Schritten verschieben.
 * Der Anfang (oben) bleibt fest, damit nichts „wegdreht“.
 *
 * Werte in ganzen Prozent, Summe immer 100. Farben kommen aus dem Theme
 * (styles/share.css), nicht aus diesem Code.
 */
import { h } from './dom.ts';

const NS = 'http://www.w3.org/2000/svg';
const R = 80;
const C = 2 * Math.PI * R;

export interface RingPart {
  id: string;
  label: string;
}

export interface ShareRing {
  el: HTMLElement;
  /** Neue Werte (Prozent je id) – z. B. nachdem ein Zahlenfeld geaendert wurde. */
  set(values: Record<string, number>): void;
}

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/** Punkt auf dem Kreis fuer einen Anteil 0..100 (0 = oben, im Uhrzeigersinn). */
function point(pct: number): { x: number; y: number } {
  const a = (pct / 100) * 2 * Math.PI;
  return { x: 100 + R * Math.sin(a), y: 100 - R * Math.cos(a) };
}

export function shareRing(opts: {
  parts: RingPart[];
  values: Record<string, number>;
  editable: boolean;
  /** Text in der Mitte, z. B. „2,4 MB“. */
  center: string;
  sub: string;
  onChange: (values: Record<string, number>) => void;
}): ShareRing {
  const { parts, editable } = opts;
  let values = { ...opts.values };

  const root = svg('svg', { viewBox: '0 0 200 200', class: 'ring__svg', role: 'img', 'aria-label': 'Aufteilung' });
  root.append(svg('circle', { cx: 100, cy: 100, r: R, class: 'ring__track' }));
  const arcs = parts.map((_, i) => {
    const c = svg('circle', { cx: 100, cy: 100, r: R, class: `ring__arc share-c${i % 6}`, transform: 'rotate(-90 100 100)' });
    root.append(c);
    return c;
  });
  const handles = parts.slice(1).map((p, i) => {
    const g = svg('circle', { r: 11, class: 'ring__handle', tabindex: editable ? 0 : -1, role: 'slider', 'aria-label': `Grenze vor ${p.label}` });
    if (editable) root.append(g);
    return { el: g, index: i + 1 };
  });

  const centerEl = h('div', { class: 'ring__center' }, h('strong', { text: opts.center }), h('span', { text: opts.sub }));
  const el = h('div', { class: 'ring' + (editable ? ' ring--editable' : '') }, root as unknown as HTMLElement, centerEl);

  /** Summe der Anteile vor Teil i (0..100). */
  const cum = (i: number) => parts.slice(0, i).reduce((n, p) => n + (values[p.id] ?? 0), 0);

  function paint(): void {
    parts.forEach((p, i) => {
      const v = values[p.id] ?? 0;
      // Kleine Luecke zwischen den Teilen – nur wenn der Teil gross genug ist.
      const gap = v > 2 ? 1.2 : 0;
      const len = Math.max(0, (v / 100) * C - gap);
      arcs[i]!.setAttribute('stroke-dasharray', `${len} ${C}`);
      arcs[i]!.setAttribute('stroke-dashoffset', String(-((cum(i) / 100) * C + gap / 2)));
      arcs[i]!.style.opacity = v === 0 ? '0' : '1';
    });
    for (const hd of handles) {
      const at = cum(hd.index);
      const pt = point(at);
      hd.el.setAttribute('cx', String(pt.x));
      hd.el.setAttribute('cy', String(pt.y));
      hd.el.setAttribute('aria-valuenow', String(at));
      hd.el.setAttribute('aria-valuetext', `${parts[hd.index - 1]!.label} ${values[parts[hd.index - 1]!.id]} %, ${parts[hd.index]!.label} ${values[parts[hd.index]!.id]} %`);
    }
  }

  /** Grenze vor Teil k auf pct setzen – nur die zwei Nachbarn aendern sich. */
  function moveBoundary(k: number, pct: number): void {
    const lo = cum(k - 1);
    const hi = cum(k + 1);
    const next = Math.round(Math.min(hi, Math.max(lo, pct)));
    const a = parts[k - 1]!.id;
    const b = parts[k]!.id;
    if (values[a] === next - lo) return;
    values = { ...values, [a]: next - lo, [b]: hi - next };
    paint();
    opts.onChange(values);
  }

  if (editable) {
    for (const hd of handles) {
      hd.el.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        hd.el.setPointerCapture(ev.pointerId);
        el.classList.add('ring--dragging');
        const move = (e: PointerEvent) => {
          const box = root.getBoundingClientRect();
          const dx = e.clientX - (box.left + box.width / 2);
          const dy = e.clientY - (box.top + box.height / 2);
          let deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
          if (deg < 0) deg += 360;
          moveBoundary(hd.index, (deg / 360) * 100);
        };
        const up = () => {
          el.classList.remove('ring--dragging');
          hd.el.removeEventListener('pointermove', move);
          hd.el.removeEventListener('pointerup', up);
          hd.el.removeEventListener('pointercancel', up);
        };
        hd.el.addEventListener('pointermove', move);
        hd.el.addEventListener('pointerup', up);
        hd.el.addEventListener('pointercancel', up);
      });
      hd.el.addEventListener('keydown', (ev) => {
        const step = ev.key === 'ArrowRight' || ev.key === 'ArrowUp' ? 1 : ev.key === 'ArrowLeft' || ev.key === 'ArrowDown' ? -1 : 0;
        if (!step) return;
        ev.preventDefault();
        moveBoundary(hd.index, cum(hd.index) + step);
      });
    }
  }

  paint();
  return {
    el,
    set(next) {
      values = { ...next };
      paint();
    },
  };
}
